import React from "react";

// ============= Types =============
export interface StatCardConfig {
  label: string;
  value: string;
}

export interface EntityDetailsStatConfig {
  stats: StatCardConfig[];
  emptyStateConfig?: {
    icon?: React.ReactNode;
    message?: string;
  };
  gridConfig?: {
    mobile?: string;
    desktop?: string;
  };
}

// ============= EmptyState Component =============
interface EmptyStateProps {
  icon?: React.ReactNode;
  message?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  message = "Aucune information disponible"
}) => (
  <div className="w-full p-6 text-center text-gray-500 bg-gray-50/50 rounded-xl border border-gray-100">
    <div className="text-gray-400 mb-2">
      {icon || (
        <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      )}
    </div>
    <p className="text-sm font-medium">{message}</p>
  </div>
);

// ============= StatCard Component =============
interface StatCardProps {
  label: string;
  value: string;
  isMobile?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  isMobile = false
}) => {
  const cardClasses = `
    flex flex-col ${isMobile ? 'p-3 sm:p-4' : 'p-4'}
    bg-white/80 backdrop-blur-sm border border-gray-100/80 rounded-xl 
    shadow-md hover:shadow-lg hover:bg-white 
    transition-all duration-300 ease-out group
    ${isMobile ? 'w-40 sm:w-44 flex-shrink-0' : ''}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className={cardClasses}>
      {/* Label */}
      <div className={`${isMobile ? 'mb-2 sm:mb-3 h-5 sm:h-6' : 'mb-3 h-6'} flex items-start`}>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider leading-none">
          {label}
        </p>
      </div>

      {/* Valeur */}
      <div className={`flex-1 flex items-center ${isMobile ? 'min-h-[1.25rem] sm:min-h-[1.5rem]' : 'min-h-[1.5rem]'}`}>
        <p className={`${isMobile ? 'text-sm sm:text-base' : 'text-base'} font-bold text-gray-900 leading-tight group-hover:text-black transition-colors duration-200`}>
          {value}
        </p>
      </div>

      {/* Barre d'accent */}
      <div className={`${isMobile ? 'mt-2 sm:mt-3' : 'mt-3'} h-0.5 bg-gradient-to-r from-blue-100 to-transparent rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300`} />
    </div>
  );
};

// ============= EntityDetailsStat Component =============
interface EntityDetailsStatProps {
  config: EntityDetailsStatConfig;
  className?: string;
}

export const EntityDetailsStat: React.FC<EntityDetailsStatProps> = ({
  config,
  className = ""
}) => {
  const {
    stats,
    emptyStateConfig,
    gridConfig = {
      mobile: "flex gap-3 overflow-x-auto pb-2",
      desktop: "grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7 gap-4"
    }
  } = config;

  // État vide
  if (!stats || stats.length === 0) {
    return <EmptyState {...emptyStateConfig} />;
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Version mobile/tablette */}
      <div className="block lg:hidden">
        <div className="overflow-x-auto pb-2">
          <div className={gridConfig.mobile} style={{ minWidth: 'max-content' }}>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                label={stat.label}
                value={stat.value}
                isMobile
              />
            ))}
          </div>
        </div>
      </div>

      {/* Version desktop */}
      <div className="hidden lg:block">
        <div className={gridConfig.desktop}>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              label={stat.label}
              value={stat.value}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
